import React, { useRef, useEffect, useState } from "react";
import xlogo from "../assets/x-logo.png";
import aishwarya from "../assets/aishwarya.jpeg";
import gcon from "../assets/gcon.png";
import ooragai from "../assets/ooragai.jpeg";
import ons from "../assets/ons.jpeg";
import rype from "../assets/rype.jpeg";

import { motion } from "framer-motion";

const testimonials = [
  {
    name: "Rype",
    img: rype,
    text: "I thought we were just getting a logo... turns out we got a whole brand story! 😄 Every curve and detail had a purpose, and the creative thinking behind it really impressed us.",
    stars: 4,
    tag: "Skin care",
  },
  {
    name: "Ooragai originals",
    img: ooragai,
    text: "People came for the pickles... but first they asked, 'Who designed this packaging?' That's when we knew we made the right choice. 😄",
    stars: 4.5,
    tag: "Pickle and podis package",
  },
  {
    name: "Aishwarya foods",
    img: aishwarya,
    text: "Every post feels fresh, premium, and perfectly matches our brand. Even our customers started complimenting the designs.",
    stars: 4,
    tag: "Food Products",
  },
  {
    name: "Gcon",
    img: gcon,
    text: "I honestly didn't think AI could make construction look this premium. Every reel had people asking, 'Is this really your project?' That's when I knew they had nailed it. 😄",
    stars: 4,
    tag: "Real Estate",
  },
  {
    name: "ONS",
    img: ons,
    text: "I expected a good website... but this turned out even better. Super clean, easy to navigate, and everything is designed from a client's perspective. Finally, a website that doesn't make visitors play hide and seek! 😄",
    stars: 4,
    tag: "Mining and Export",
  },
];

const StarRating = ({ count = 4, total = 5 }) => (
  <div className="flex justify-center gap-1 mb-2">
    {Array.from({ length: total }).map((_, i) => (
      <svg
        key={i}
        width="18"
        height="18"
        viewBox="0 0 20 20"
        fill={i < count ? "#000000" : "#CFCFCF"}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.32L10 13.27l-4.77 2.51.91-5.32L2.27 6.62l5.34-.78L10 1z" />
      </svg>
    ))}
  </div>
);

const TestimonialCard = ({ name, img, text, stars, tag, className }) => (
  <div
    className={`relative bg-white rounded-sm px-8 pt-14 pb-20 w-full h-full flex flex-col overflow-visible ${
      className || ""
    }`}
  >
    {/* Watermark */}
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <img src={xlogo} alt="watermark" className="object-contain h-full" />
    </div>

    {/* Content */}
    <div className="relative z-10 text-center flex flex-col flex-1">
      <h3 className="text-[30px] font-semibold text-[#1F2937] mb-4">
        {name}
      </h3>

      <p className="text-[15px] leading-7 text-[#1F2937] mb-5 flex-1">
        {text}
      </p>

      <StarRating count={stars} />

      <p className="text-[#1F2937] text-sm font-medium">{tag}</p>
    </div>

    {/* Avatar */}
    <div className="absolute left-1/2 -bottom-10 -translate-x-1/2 z-20">
      <div className="w-[92px] h-[92px] rounded-full border-4 border-white overflow-hidden shadow-[0_18px_40px_rgba(18,154,220,0.18)]">
        <img src={img} alt="client" className="w-full h-full object-cover" />
      </div>
    </div>
  </div>
);
const InfiniteCircularRow = () => {
  // Duplicate testimonials to create a seamless loop
  const duplicated = [...testimonials, ...testimonials];
  const containerRef = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let animationFrame;
    let startTime;
    const CYCLE_DURATION = 30000; // duration of one full cycle in ms (adjust for speed)

    const getSetWidth = () => {
      if (!containerRef.current) return 0;
      const firstCard = containerRef.current.querySelector(".testimonial-card");
      if (!firstCard) return 0;
      const gap = 32; // gap-8 = 2rem = 32px
      // Width of one set = (card width + gap) * number of testimonials
      return (firstCard.offsetWidth + gap) * testimonials.length;
    };

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const setWidth = getSetWidth();

      if (setWidth === 0) {
        animationFrame = requestAnimationFrame(animate);
        return;
      }

      // Progress from 0 to 1 over CYCLE_DURATION
      const progress = (elapsed % CYCLE_DURATION) / CYCLE_DURATION;
      // Move left by one full set width across the cycle
      setOffset(-setWidth * progress);

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  return (
    <div className="relative overflow-hidden w-full py-28">
      <div
        ref={containerRef}
        className="flex flex-nowrap gap-8 transition-transform duration-0 ease-linear"
        style={{ transform: `translateX(${offset}px)` }}
      >
        {duplicated.map((item, idx) => (
          <div
            key={idx}
            className="testimonial-card flex-shrink-0 w-[320px] sm:w-[360px] md:w-[380px]"
          >
            <TestimonialCard {...item} />
          </div>
        ))}
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  return (
    <section className="bg-[#F5F5F5] pt-24 pb-36 overflow-hidden">
      <div className="mx-auto text-center">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <p
            className="text-xl font-semibold tracking-[0] uppercase mb-3"
            style={{
              display: "inline-block",
              background:
                "linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            WHAT OUR CLIENTS SAY
          </p>
          <h2
            className="text-4xl md:text-4xl font-bold text-gray-900"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Clients said it. We didn't
          </h2>
        </motion.div>

        {/* Infinite horizontal circular loop */}
        <InfiniteCircularRow />
      </div>
    </section>
  );
};

export default TestimonialsSection;